import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { UsersRound, Plus, Loader2, CalendarDays, ArrowRight, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const diff = Math.ceil((d - new Date()) / 86400000);
  return diff;
};

const countdownLabel = (dateStr) => {
  const n = daysUntil(dateStr);
  if (n === null) return null;
  if (n < 0) return 'Past';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  return `${n} days`;
};

const GroupsPage = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', event_name: '', event_date: '' });

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data || []);
    } catch (e) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Give your group a name.'); return; }
    setCreating(true);
    try {
      const res = await api.post('/groups', { ...form, event_date: form.event_date || null });
      toast.success('Group created!');
      setShowCreate(false);
      setForm({ name: '', description: '', event_name: '', event_date: '' });
      navigate(`/groups/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) return;
    setJoining(true);
    try {
      const res = await api.post(`/groups/join/${encodeURIComponent(code)}`);
      toast.success('Joined!');
      navigate(`/groups/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join with that code');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="groups-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold flex items-center gap-3">
            <UsersRound className="w-8 h-8" /> Groups
          </h1>
          <p className="mt-1">Plan a group cosplay together and track everyone's progress.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}
          className="font-semibold gap-2" data-testid="new-group-btn">
          <Plus className="w-5 h-5" /> New group
        </Button>
      </div>

      {/* Join with code */}
      <form onSubmit={join} className="flex gap-3 max-w-md">
        <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Have an invite code? Paste it here"
          data-testid="join-code-input" />
        <Button type="submit" disabled={joining} variant="outline">
          {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Join'}
        </Button>
      </form>

      {groups.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <UsersRound className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-lg font-heading font-semibold mb-2">No groups yet</h3>
          <p className="mb-6">Create a group for your next squad build, or join one with an invite code.</p>
          <Button onClick={() => setShowCreate(true)} className="font-semibold gap-2">
            <Plus className="w-5 h-5" /> New group
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map((g) => {
            const cd = countdownLabel(g.event_date);
            return (
              <Link key={g.id} to={`/groups/${g.id}`} className="glass-card p-6 overflow-hidden group" data-testid={`group-card-${g.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-semibold text-lg truncate flex items-center gap-2">
                      {g.is_owner && <Crown className="w-4 h-4 flex-shrink-0" />}
                      {g.name}
                    </h3>
                    {g.event_name && <p className="text-sm truncate">{g.event_name}</p>}
                  </div>
                  {cd && (
                    <span className="text-xs font-medium flex-shrink-0 whitespace-nowrap inline-flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" /> {cd}
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{g.member_count} member{g.member_count !== 1 ? 's' : ''} · readiness</span>
                    <span className="font-mono">{g.readiness}%</span>
                  </div>
                  <Progress value={g.readiness} className="h-2" />
                </div>
                <div className="mt-4 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100">
                  Open <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-heading">New group</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Group name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Mando Mercs @ DragonCon" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event</Label>
                <Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                  placeholder="Con or meetup" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  />
              </div>
            </div>
            <Button type="submit" disabled={creating}
              className="w-full font-semibold">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create group'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsPage;
